import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import enhancedDataService from '../utils/enhancedDataService';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// SECURITY: never expose the service on window in production
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__tvDataService = enhancedDataService; // dev-only
}

const GlobalDataContext = createContext();

export const GlobalDataProvider = ({ children }) => {
  const [savedWebinarIds, setSavedWebinarIds] = useState([]);
  const queryClient = useQueryClient();

  // Fetch saved webinars
  const fetchSavedWebinars = useCallback(async (email) => {
    if (!email) {
      setSavedWebinarIds([]);
      return;
    }
    const ids = await enhancedDataService.getSavedWebinars(email);
    setSavedWebinarIds(ids || []);
  }, []);

  useEffect(() => {
    const checkAndFetch = () => {
      try {
        const raw = localStorage.getItem('auth_session');
        if (raw) {
          const s = JSON.parse(raw);
          if (s?.verified && s?.email) {
            fetchSavedWebinars(s.email);
            return;
          }
        }
      } catch (_) {}
      setSavedWebinarIds([]);
    };

    checkAndFetch();

    // Listen for auth events to fetch or clear bookmarks
    const handleAuthChange = () => {
      checkAndFetch();
    };

    window.addEventListener('auth-state-change', handleAuthChange);
    window.addEventListener('globalauth-change', handleAuthChange);
    window.addEventListener('focus', checkAndFetch);

    return () => {
      window.removeEventListener('auth-state-change', handleAuthChange);
      window.removeEventListener('globalauth-change', handleAuthChange);
      window.removeEventListener('focus', checkAndFetch);
    };
  }, [fetchSavedWebinars]);

  const savePendingRef = useRef(new Set());

  const toggleSaveWebinar = useCallback(async (webinarId) => {
    if (savePendingRef.current.has(webinarId)) return;
    savePendingRef.current.add(webinarId);

    let email = null;
    try {
      const raw = localStorage.getItem('auth_session');
      if (raw) {
        email = JSON.parse(raw)?.email;
      }
    } catch (_) {}

    if (!email) {
      toast.error('Please sign in to save webinars');
      savePendingRef.current.delete(webinarId);
      return;
    }

    const isCurrentlySaved = savedWebinarIds.includes(webinarId);
    
    // Optimistic Update
    setSavedWebinarIds(prev => 
      isCurrentlySaved 
        ? prev.filter(id => id !== webinarId) 
        : [...prev, webinarId]
    );

    // Call API
    let success = false;
    try {
      if (isCurrentlySaved) {
        success = await enhancedDataService.unsaveWebinar(email, webinarId);
        if (success) {
          toast.success('Removed from saved webinars');
        }
      } else {
        success = await enhancedDataService.saveWebinar(email, webinarId);
        if (success) {
          toast.success('Saved to your webinars');
        }
      }
    } catch (err) {
      console.warn('[GlobalDataContext] toggleSaveWebinar error:', err.message);
    } finally {
      savePendingRef.current.delete(webinarId);
    }

    // Rollback on failure
    if (!success) {
      toast.error('Failed to update bookmark');
      setSavedWebinarIds(prev => 
        isCurrentlySaved 
          ? [...prev, webinarId] 
          : prev.filter(id => id !== webinarId)
      );
    }
  }, [savedWebinarIds]);

  const refreshData = useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);

  // Realtime subscription for forum_posts table
  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel('public:forum_posts_updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'forum_posts',
        filter: 'status=eq.active'
      }, (payload) => {
        if (payload.eventType === 'UPDATE') {
          queryClient.setQueryData(['posts'], (oldPosts) => {
            if (!oldPosts) return [];
            return oldPosts.map(post => 
              post && post.id === payload.new.id ? { ...post, ...payload.new } : post
            );
          });
        } else if (payload.eventType === 'INSERT') {
          queryClient.setQueryData(['posts'], (oldPosts) => {
            if (!oldPosts) return [payload.new];
            if (oldPosts.some(p => p && p.id === payload.new.id)) return oldPosts;
            return [payload.new, ...oldPosts];
          });
        } else if (payload.eventType === 'DELETE') {
          queryClient.setQueryData(['posts'], (oldPosts) => {
            if (!oldPosts) return [];
            return oldPosts.filter(post => post && post.id !== payload.old.id);
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const providerValue = useMemo(() => ({
    savedWebinarIds,
    toggleSaveWebinar,
    refreshData
  }), [savedWebinarIds, toggleSaveWebinar, refreshData]);

  return (
    <GlobalDataContext.Provider value={providerValue}>
      {children}
    </GlobalDataContext.Provider>
  );
};

export const useGlobalData = () => {
  const context = useContext(GlobalDataContext);
  if (!context) {
    return {
      savedWebinarIds: [],
      toggleSaveWebinar: () => Promise.resolve(),
      refreshData: () => Promise.resolve()
    };
  }
  return context;
};

// Specialized hooks
export const useWebsiteData = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['website'],
    queryFn: enhancedDataService.getWebsiteContent,
    staleTime: 5 * 60 * 1000,
  });
  return React.useMemo(() => ({ data: data || {}, loading: isLoading }), [data, isLoading]);
};

export const useCommunityData = () => {
  const queryClient = useQueryClient();

  const { data: posts, isLoading: postsLoading, refetch: refetchPosts } = useQuery({
    queryKey: ['posts'],
    queryFn: enhancedDataService.getForumPosts,
    staleTime: 2 * 60 * 1000,
  });

  const { data: categories, isLoading: categoriesLoading, refetch: refetchCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: enhancedDataService.getForumCategories,
    staleTime: 5 * 60 * 1000,
  });

  const { data: userProfiles, isLoading: profilesLoading, refetch: refetchProfiles } = useQuery({
    queryKey: ['userProfiles'],
    queryFn: enhancedDataService.getUserProfiles,
    staleTime: 5 * 60 * 1000,
  });

  const postsWithProfiles = React.useMemo(() => {
    if (!Array.isArray(posts)) return [];
    return posts.map(post => {
      if (!post) return null;
      const profile = Array.isArray(userProfiles)
        ? userProfiles.find(p => p && (p.id === post.user_id || p.email === post.user_email))
        : null;
      return {
        ...post,
        user_profiles: profile || null
      };
    }).filter(Boolean);
  }, [posts, userProfiles]);

  const loading = postsLoading || categoriesLoading || profilesLoading;

  const refreshData = useCallback(async () => {
    await Promise.allSettled([refetchPosts(), refetchCategories(), refetchProfiles()]);
  }, [refetchPosts, refetchCategories, refetchProfiles]);

  const prependPost = useCallback((newPost) => {
    queryClient.setQueryData(['posts'], (oldPosts) => {
      if (!oldPosts) return [newPost];
      return [newPost, ...oldPosts];
    });
  }, [queryClient]);

  return React.useMemo(() => ({
    posts: postsWithProfiles,
    categories: categories || [],
    loading,
    refreshData,
    prependPost
  }), [postsWithProfiles, categories, loading, refreshData, prependPost]);
};

export const useEventsData = () => {
  const { data: events, isLoading: loading } = useQuery({
    queryKey: ['events'],
    queryFn: enhancedDataService.getUpcomingEvents,
    staleTime: 2 * 60 * 1000,
  });
  return React.useMemo(() => ({ events: events || [], loading }), [events, loading]);
};

export const useWinnersData = () => {
  const { data: winners, isLoading: loading } = useQuery({
    queryKey: ['winners'],
    queryFn: enhancedDataService.getContestSubmissions,
    staleTime: 5 * 60 * 1000,
  });
  return React.useMemo(() => ({ winners: winners || [], loading }), [winners, loading]);
};

export const useSavedWebinarsData = (userEmail, savedIds) => {
  const { data: savedWebinars, isLoading: loading } = useQuery({
    queryKey: ['saved-webinars-details', userEmail, savedIds],
    queryFn: () => enhancedDataService.getSavedWebinarsDetails(savedIds),
    enabled: Boolean(userEmail && Array.isArray(savedIds) && savedIds.length > 0),
    staleTime: 5 * 60 * 1000,
  });
  return React.useMemo(() => ({ savedWebinars: savedWebinars || [], loading }), [savedWebinars, loading]);
};